/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase.js';

const AdminStatusSetter = ({ userIsAdmin }) => {
  const [userEmail, setUserEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  if (!userIsAdmin) {
    return null;
  }

  const handleSetAdminClaim = async () => {
    if (!userEmail.trim()) {
      setMessage('❌ Please enter a user email');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('🔐 Attempting to call setAdminClaim function...');
      console.log('Current user:', auth.currentUser?.email);
      
      const functions = getFunctions();
      const setAdminClaim = httpsCallable(functions, 'setAdminClaim');
      
      console.log('Calling function with:', { email: userEmail.trim(), isAdmin });
      const result = await setAdminClaim({ email: userEmail.trim(), isAdmin });
      
      console.log('✅ Function result:', result.data);

      // Update isAdmin in Firestore user document
      await updateDoc(doc(db, 'users', result.data.userId), {
        isAdmin: isAdmin
      });
      console.log('✅ Firestore user document updated');

      setMessage(`✅ ${result.data.message}`);
      setUserEmail('');
      
    } catch (error) {
      console.error('❌ Full error object:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="admin-management">
      <h3>Admin Management</h3>
      <p>
        Set or revoke admin status for users. User must log out and back in for changes to take effect.
      </p>
      
      <div style={{ marginTop: '20px', textAlign:'left' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>
          User Email:
          <input
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            placeholder="user@example.com"
            disabled={loading}
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', marginTop: '12px' }}>
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={(e) => setIsAdmin(e.target.checked)}
            disabled={loading}
            style={{ marginRight: '8px' }}
          />
          <span>Grant admin status</span>
        </label>

        <button
          onClick={handleSetAdminClaim}
          disabled={loading}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: isAdmin ? '#28a745' : '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Processing...' : isAdmin ? 'Grant Admin' : 'Revoke Admin'}
        </button>

        {message && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            borderRadius: '4px',
            backgroundColor: message.includes('✅') ? '#d4edda' : '#f8d7da',
            color: message.includes('✅') ? '#155724' : '#721c24',
            border: `1px solid ${message.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStatusSetter;